import React from 'react';
import PropTypes from 'prop-types';
import Link from '@components/shared/Link';
import { useAppContext } from '@/context/AppContext';
import { iconBoxStyle, iconGlyph } from '@/theme/icons';
import { moduleBox } from '@/theme/header-modules';
import { resolveGlyph } from '@/library';
import GlyphPreview from '@components/library/GlyphPreview';
import useChromeLayer from '@/hooks/useChromeLayer';
import { useViewerTheme } from '../viewerTheme';
import { previewLibrary, useHoverGlyph } from '@/preview/hoverChrome';
import { chromeCornerClass } from '@/slideshow/chrome';
import { VIEWER_CONTROL_BACKDROP, VIEWER_CONTROL_STYLE } from '@/theme/viewer-look';

function useIcon(id) {
    const { content } = useAppContext();
    const theme = useViewerTheme();
    const layer = useChromeLayer();
    const hoverGlyph = useHoverGlyph(id);
    const modules = theme?.header?.modules;
    const box = moduleBox(modules, id, layer);
    const library = previewLibrary(content);
    const stored = iconGlyph(modules, id, layer);
    const glyphId = hoverGlyph !== undefined ? (hoverGlyph || stored) : stored;
    const glyph = resolveGlyph(theme?.icons?.set, glyphId, library);
    return { glyph, style: iconBoxStyle(box) };
}

/**
 * Previous navigation link (left half of screen).
 * Left half is the clickable area; arrow aligned vertically with right arrow, same distance from left as right from right (pl-5 md:pl-[30px]).
 * When fillContainer, the link fills its parent (e.g. a 50vw overlay wrapper).
 * When iconOnly, only the arrow takes the pointer and the rest of the half lets it through.
 * When letThrough (the pointer is over a block on the slide), none of it does.
 */
export const PrevLink = ({ url, onClick, fillContainer = false, iconOnly = false, letThrough = false }) => {
    if (!url) return null;

    const linkClassName = fillContainer
        ? 'absolute inset-0 z-10 flex items-center justify-start pl-5 md:pl-[30px] hover:no-underline transition-colors duration-200 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0'
        : 'absolute inset-y-0 left-0 w-1/2 z-10 flex items-center justify-start pl-5 md:pl-[30px] hover:no-underline transition-colors duration-200 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0';

    return (
        <Link 
            className={`${linkClassName}${iconOnly || letThrough ? ' pointer-events-none' : ''}`}
            style={VIEWER_CONTROL_STYLE}
            to={url}
            onClick={onClick}
            aria-label="Previous image"
        >
            <IconHandle id="icon-prev" iconOnly={iconOnly && !letThrough} />
        </Link>
    );
};

PrevLink.propTypes = {
    url: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    fillContainer: PropTypes.bool,
    iconOnly: PropTypes.bool,
    letThrough: PropTypes.bool,
};

/**
 * Next navigation link (right half of screen).
 * Right half is the clickable area; arrow on right edge (pr-5 md:pr-[30px]), vertically centered.
 * When fillContainer, the link fills its parent (e.g. a 50vw overlay wrapper).
 * When iconOnly, only the arrow takes the pointer and the rest of the half lets it through.
 * When letThrough (the pointer is over a block on the slide), none of it does.
 */
export const NextLink = ({ url, onClick, fillContainer = false, iconOnly = false, letThrough = false }) => {
    if (!url) return null;

    const linkClassName = fillContainer
        ? 'absolute inset-0 z-10 flex items-center justify-end pr-5 md:pr-[30px] hover:no-underline transition-colors duration-200 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0'
        : 'absolute inset-y-0 right-0 w-1/2 z-10 flex items-center justify-end pr-5 md:pr-[30px] hover:no-underline transition-colors duration-200 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0';

    return (
        <Link 
            className={`${linkClassName}${iconOnly || letThrough ? ' pointer-events-none' : ''}`}
            style={VIEWER_CONTROL_STYLE}
            to={url}
            onClick={onClick}
            aria-label="Next image"
        >
            <IconHandle id="icon-next" iconOnly={iconOnly && !letThrough} />
        </Link>
    );
};

NextLink.propTypes = {
    url: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    fillContainer: PropTypes.bool,
    iconOnly: PropTypes.bool,
    letThrough: PropTypes.bool,
};

/**
 * Close button, top right unless a slideshow puts it in another corner.
 */
export const CloseButton = ({ url, onClick, corner = 'tr' }) => {
    if (corner === 'hidden') return null;
    return (
        <Link
            data-slideshow-close=""
            className={`absolute ${chromeCornerClass('close', corner)} z-20 flex items-center justify-center p-5 min-w-[44px] min-h-[44px] hover:no-underline transition-colors duration-200 pointer-events-auto`}
            style={VIEWER_CONTROL_STYLE}
            to={url}
            onClick={onClick}
            aria-label="Close image viewer"
        >
            <IconHandle id="icon-close" />
        </Link>
    );
};

CloseButton.propTypes = {
    url: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    corner: PropTypes.oneOf(['tl', 'tr', 'bl', 'br', 'hidden']),
};

function IconHandle({ id, iconOnly = false }) {
    const { glyph, style } = useIcon(id);
    return (
        <span
            data-header-module={id}
            data-lock-ratio="0"
            className={`relative z-[1] inline-flex items-center justify-center p-[4px]${iconOnly ? ' pointer-events-auto' : ''}`}
            style={{ ...VIEWER_CONTROL_BACKDROP, ...style }}
        >
            <GlyphPreview glyph={glyph} />
        </span>
    );
}

IconHandle.propTypes = { id: PropTypes.string.isRequired, iconOnly: PropTypes.bool };

export default { PrevLink, NextLink, CloseButton };
